import { UsernameLookupResult } from '@/client/users';
import { LoadingStateType, LoadingStatuses } from '@/common/constants';
import { AppButton } from '@/ui/buttons';
import { getDaysUntilRemoval } from '@/utils/helpers/dates.helper';
import { InfoCircleIcon } from '@/utils/helpers/icons.helper';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

const DEFAULT_GRACE_PERIOD_DAYS = 30;

const Callout = styled.div`
  background: var(--yellow-pale);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding-small);
  margin-bottom: var(--default-padding);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: var(--xxsmall-padding);
  font-size: var(--small-font-size);
  font-weight: 700;

  svg {
    flex-shrink: 0;
  }
`;

const Body = styled.p`
  font-size: var(--xsmall-font-size);
  line-height: 1.4;
  margin: var(--xxsmall-padding) 0 0;
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: var(--semi-medium-padding);
  margin-top: var(--medium-padding);
`;

const Option = styled.div`
  display: grid;
  justify-items: center;
  gap: var(--small-padding);
  background: var(--white-main);
  border: 1px solid var(--yellow-border);
  border-radius: var(--semi-medium-border-radius);
  padding: var(--default-padding-small);
  text-align: center;

  i {
    font-size: var(--default-font-size);
  }

  p {
    font-size: var(--xsmall-font-size);
    line-height: 1.4;
    margin: 0;
  }
`;

type UsernameConflictCalloutProps = {
  username: string;
  existingUser: UsernameLookupResult;
  isCreate?: boolean;
  onRestore: (name: string) => void;
  onDeletePermanently: () => void;
  restoreState?: LoadingStateType;
  deleteState?: LoadingStateType;
};

export const UsernameConflictCallout: React.FC<
  UsernameConflictCalloutProps
> = ({
  username,
  existingUser,
  isCreate = false,
  onRestore,
  onDeletePermanently,
  restoreState = LoadingStatuses.IDLE,
  deleteState = LoadingStatuses.IDLE,
}) => {
  const { t } = useTranslation();

  const { deletedAt } = existingUser;

  if (!deletedAt) return null;

  const name =
    `${existingUser.firstName ?? ''} ${existingUser.lastName ?? ''}`.trim() ||
    username;

  const daysRemaining = Math.max(
    0,
    getDaysUntilRemoval(
      deletedAt,
      existingUser.gracePeriodDays ?? DEFAULT_GRACE_PERIOD_DAYS,
    ),
  );

  return (
    <Callout>
      <Header>
        <InfoCircleIcon />
        {t('user.usernameConflict.title')}
      </Header>

      <Body>
        {t(
          isCreate
            ? 'user.usernameConflict.body'
            : 'user.usernameConflict.bodyEdit',
          { username, name, count: daysRemaining },
        )}
      </Body>

      <Options>
        {isCreate && (
          <Option>
            <i className="pi pi-user-plus" />
            <p>
              <Trans
                i18nKey="user.usernameConflict.restore.description"
                values={{ name }}
                components={{ bold: <strong /> }}
              />
            </p>
            <AppButton
              label={t('button.restoreUser', { count: 1 })}
              severity="danger"
              state={restoreState}
              onClick={() => onRestore(name)}
            />
          </Option>
        )}

        <Option>
          <i className="pi pi-trash" />
          <p>
            <Trans
              i18nKey="user.usernameConflict.delete.description"
              values={{ name }}
              components={{ bold: <strong /> }}
            />
          </p>
          <AppButton
            label={t('user.deletePermanently')}
            severity="danger"
            state={deleteState}
            onClick={onDeletePermanently}
          />
        </Option>
      </Options>
    </Callout>
  );
};
